import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Lock } from "lucide-react";

const ACCESS_CODE = "1515";
// Public routes that MUST bypass the gate (wall displays, vendor + signer + share links).
const PUBLIC_PREFIXES = ["/display/", "/vendor/", "/sign/", "/share/", "/r/"];

export default function AccessCodeGate({ children }) {
  const location = useLocation();
  const isPublicRoute = PUBLIC_PREFIXES.some(p => location.pathname.startsWith(p));
  // Always re-prompt on every page load — unlocked state is per-session (in-memory only).
  const [unlocked, setUnlocked] = useState(false);
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (!unlocked) refs[0].current?.focus();
  }, [unlocked]);

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    setError(false);
    if (clean && i < 3) refs[i + 1].current?.focus();
    if (next.every(d => d) && next.join("") === ACCESS_CODE) {
      setUnlocked(true);
    } else if (next.every(d => d)) {
      setError(true);
      setTimeout(() => { setDigits(["", "", "", ""]); refs[0].current?.focus(); }, 600);
    }
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  if (unlocked || isPublicRoute) return children;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-[#0F172A] to-[#001a2c]" data-testid="access-gate">
      <div className="bg-white rounded-xl shadow-2xl p-10 w-full max-w-sm mx-4">
        <div className="flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-[#005289] flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Restricted Access</h1>
          <p className="text-sm text-slate-500 mb-6 text-center">Enter the 4-digit access code to continue.</p>
          <div className={`flex gap-3 mb-2 ${error ? "animate-pulse" : ""}`}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={refs[i]}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={e => setDigit(i, e.target.value)}
                onKeyDown={e => handleKey(i, e)}
                className={`w-14 h-16 text-center text-2xl font-bold border-2 rounded-lg focus:outline-none ${error ? "border-red-500 text-red-600" : "border-slate-200 focus:border-[#005289] text-slate-900"}`}
                data-testid={`access-digit-${i}`}
              />
            ))}
          </div>
          {error && <div className="text-xs text-red-600 mt-2" data-testid="access-error">Incorrect code. Try again.</div>}
        </div>
      </div>
    </div>
  );
}
